/**
 * useFeatureFlags - Hook for accessing feature flag configuration
 *
 * Provides a React-friendly interface to the feature flags defined in app.config.ts.
 * All feature flags are sourced from the centralized configuration system.
 *
 * Usage:
 * ```tsx
 * const { timeMachine, journalView, photoFullscreen } = useFeatureFlags();
 * if (timeMachine) { ... }
 * ```
 */
import { useMemo } from 'react';
import { getAppConfig, isFeatureEnabled, type AppConfig } from '../config/app.config';

export type FeatureFlags = AppConfig['features'];

/**
 * Hook to access all feature flags.
 * Returns a memoized snapshot of the current feature flag configuration.
 */
export function useFeatureFlags(): FeatureFlags {
  return useMemo(() => getAppConfig().features, []);
}

/**
 * Hook to check a single feature flag.
 * Convenience wrapper around isFeatureEnabled for use in components.
 */
export function useFeature(feature: keyof FeatureFlags): boolean {
  return useMemo(() => isFeatureEnabled(feature), [feature]);
}

export { isFeatureEnabled };
